import { useState, useRef } from "react";
import { useQueryClient, useMutation } from "@tanstack/react-query";
import Sidebar from "./Sidebar";
import ChatInterface from "./ChatInterface";
import AllChatsPanel from "./AllChatsPanel";
import AttachLinkModal from "./AttachLinkModal";
import ModelSwitcher from "./ModelSwitcher";
import { uploadFile } from "../api/files";
import { useToast } from "../context/ToastContext";

const ACTIVE_CHAT_KEY = "docmind:active_chat_id";

export default function Layout() {
  const [selectedChatId, setSelectedChatIdRaw] = useState(
    () => localStorage.getItem(ACTIVE_CHAT_KEY) || null
  );
  const setSelectedChatId = (id) => {
    setSelectedChatIdRaw(id);
    if (id) localStorage.setItem(ACTIVE_CHAT_KEY, id);
    else localStorage.removeItem(ACTIVE_CHAT_KEY);
  };
  const [selectedFileIds, setSelectedFileIds] = useState([]);
  const [mode, setMode] = useState("qa");
  const [showAllChats, setShowAllChats] = useState(
    () => !localStorage.getItem(ACTIVE_CHAT_KEY)
  );
  const [showAttachLink, setShowAttachLink] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef(null);
  const dragCounterRef = useRef(0);
  const { toast } = useToast();
  const qc = useQueryClient();

  const uploadMut = useMutation({
    mutationFn: (file) => uploadFile(file),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ["files"] });
      toast("File uploaded — indexing in background", "success");
      const fileId = res.data?.data?.id;
      if (fileId) setSelectedFileIds((prev) => [...prev, fileId]);
    },
    onError: (err) =>
      toast(err.response?.data?.message || "Upload failed", "error"),
  });

  const handleNewChat = () => {
    setSelectedChatId(null);
    setSelectedFileIds([]);
    setMode("qa");
    setShowAllChats(false);
  };

  const handleSelectChat = (id) => {
    setSelectedChatId(id);
    setSelectedFileIds([]);
    setShowAllChats(false);
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) uploadMut.mutate(file);
    e.target.value = "";
  };

  const handleRequestUpload = () => fileInputRef.current?.click();

  const handleDragEnter = (e) => {
    if (!e.dataTransfer?.types?.includes("Files")) return;
    e.preventDefault();
    dragCounterRef.current += 1;
    if (dragCounterRef.current === 1) setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    dragCounterRef.current = Math.max(0, dragCounterRef.current - 1);
    if (dragCounterRef.current === 0) setIsDragOver(false);
  };

  const handleDragOver = (e) => {
    if (e.dataTransfer?.types?.includes("Files")) e.preventDefault();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    dragCounterRef.current = 0;
    setIsDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) uploadMut.mutate(file);
  };

  return (
    <div className="flex h-screen bg-dark-bg text-dark-text overflow-hidden">
      {/* Left Sidebar (collapses to icon-rail) */}
      <Sidebar
        selectedChatId={selectedChatId}
        onSelectChat={handleSelectChat}
        onNewChat={handleNewChat}
        onOpenAllChats={() => setShowAllChats(true)}
        onOpenAttachLink={() => setShowAttachLink(true)}
        selectedFileIds={selectedFileIds}
        onSelectionChange={setSelectedFileIds}
        collapsed={!sidebarOpen}
        onToggle={() => setSidebarOpen((o) => !o)}
      />

      {/* Main area */}
      <div
        className="relative flex flex-1 flex-col min-w-0 overflow-hidden"
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        {/* Top bar — no border, clean Gemini-style */}
        <div className="flex items-center justify-between px-6 py-3 flex-shrink-0">
          {/* Left: title + mode (hamburger lives in sidebar) */}
          <div className="flex items-center gap-3">
            <span className="text-base font-semibold tracking-wide">DocMind</span>
            <span className="text-[#333]">|</span>
            <span className="text-xs text-dark-muted font-medium tracking-wider">MODE</span>
            <button
              onClick={() => setMode("qa")}
              className={`rounded-full px-4 py-1 text-xs font-semibold transition-colors ${
                mode === "qa"
                  ? "bg-accent/15 text-accent border border-accent/25"
                  : "border border-[#444] text-[#888] hover:border-[#666] hover:text-dark-text"
              }`}
            >
              Q&amp;A
            </button>
            <button
              onClick={() => setMode("review")}
              className={`rounded-full px-4 py-1 text-xs font-semibold transition-colors ${
                mode === "review"
                  ? "bg-accent/15 text-accent border border-accent/25"
                  : "border border-[#444] text-[#888] hover:border-[#666] hover:text-dark-text"
              }`}
            >
              Review
            </button>
            <span className="text-[#333]">|</span>
            <ModelSwitcher />
          </div>

          {/* Right: share */}
          <button className="text-dark-muted hover:text-dark-text transition-colors">
            <ion-icon name="share-social-outline" style={{ fontSize: "19px" }} />
          </button>
        </div>

        {/* Chat area */}
        <div className="flex flex-1 flex-col min-h-0">
          <ChatInterface
            chatId={selectedChatId}
            selectedFileIds={selectedFileIds}
            mode={mode}
            onChatCreated={(id) => {
              setSelectedChatId(id);
              qc.invalidateQueries({ queryKey: ["chats"] });
            }}
            onRequestUpload={handleRequestUpload}
            onOpenAttachLink={() => setShowAttachLink(true)}
            onChatNotFound={() => setSelectedChatId(null)}
          />
        </div>

        {/* Drag overlay */}
        {isDragOver && (
          <div className="pointer-events-none absolute inset-0 z-30 flex items-center justify-center bg-dark-bg/85">
            <div className="flex flex-col items-center gap-2 rounded-2xl border-2 border-dashed border-accent/60 bg-accent/5 px-12 py-10">
              <ion-icon name="cloud-upload-outline" style={{ fontSize: "40px", color: "#22c55e" }} />
              <span className="text-sm font-medium text-accent">Drop file to upload</span>
            </div>
          </div>
        )}

        {/* All Chats overlay */}
        {showAllChats && (
          <div className="absolute inset-0 z-20 bg-dark-bg flex flex-col">
            <AllChatsPanel
              selectedChatId={selectedChatId}
              onSelectChat={handleSelectChat}
              onNewChat={handleNewChat}
              onClose={() => setShowAllChats(false)}
            />
          </div>
        )}
      </div>

      {/* Attach Link modal */}
      {showAttachLink && (
        <AttachLinkModal
          onClose={() => setShowAttachLink(false)}
          onFileAdded={(id) => setSelectedFileIds((prev) => [...prev, id])}
        />
      )}

      {/* Hidden file input — triggered by the chat "+" button */}
      <input
        ref={fileInputRef}
        type="file"
        className="hidden"
        onChange={handleFileChange}
        accept=".pdf,.docx,.epub,.txt,.mp3,.wav,.mp4"
      />
    </div>
  );
}
